import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Container } from 'typescript-ioc';
import { ConnectionType, IConnectionDetailsDTO } from '../../../../../api/connections.dao';
import { Popover } from '../../../../global/popover/popover';
import { WebinarReportsViewModel } from '../webinar-report/webinar-reports.viewmodel';
import TextWithCopyBtn from './_text-with-copy-btn/text-with-copy-btn';
import { CopyButton } from '../../../../global/copy-button/copy-button';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../../tracking-service/tracking-values';
import { MeetingApiParams } from './_connection-form/connection-form.viewmodel';
import { ConnectionDetailsPopoverOptionsButton } from './_connection-details-popover-options-button';
import { LinkTargetValue } from '../../../../../constants/common.constants';
import { ConnectionDetailsPopoverContent } from './_connection-details-popover-content';
import { composeConnectionDetailsCopyAllMessage } from '../../../../../infrastructure/utils/connections.utils';
import { IZoomDAO } from '../../../../../api/zoom.dao';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';

export interface IProps {
    connection: IConnectionDetailsDTO;
    handleOnChange: (connection: IConnectionDetailsDTO) => void;
    showConfigurePassportForm: (connection: IConnectionDetailsDTO) => void;
    setDeletePopoverConnection: (connection: IConnectionDetailsDTO | null) => void;
    isPopoverOpen: boolean;
    setIsPopoverOpen: (isPopoverOpen: boolean) => void;
    listItemRef: React.MutableRefObject<HTMLElement>;
    itemWidth?: number;
    cloneConnection: (connection: IConnectionDetailsDTO) => void;
    webinarReportsVM: WebinarReportsViewModel;
    handleTrackingEvent?: (event: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => void;
    handleTroubleshoot: (connection: IConnectionDetailsDTO) => void;
    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom?: OpenFrom) => void;
    meetingParams: MeetingApiParams;
    onOpenKnovioPreviewLinkPopover: () => void;
    shouldDisableDeleteButton: boolean;
    knovioPreCheckLoading?: boolean;
}

const ConnectionDetailsPopover = (props: IProps) => {
    const {
        connection,
        cloneConnection,
        handleOnChange,
        showConfigurePassportForm,
        isPopoverOpen,
        setIsPopoverOpen,
        listItemRef,
        itemWidth,
        setDeletePopoverConnection,
        webinarReportsVM,
        handleTrackingEvent,
        handleTroubleshoot,
        trackHostJoinClick,
        onOpenKnovioPreviewLinkPopover,
        shouldDisableDeleteButton,
        knovioPreCheckLoading
    } = props;

    const { hostLink, connectionLink } = connection;
    const zoomDao = Container.get(IZoomDAO);

    const [audioProviderLabel, setAudioProviderLabel] = useState('');

    useEffect(() => {
        if (connection.type === ConnectionType.AudioBridge && connection.audioProviderId && isPopoverOpen) {
           fetchTelephoneNumberSets();
        }
    }, [isPopoverOpen]);

    const fetchTelephoneNumberSets = async (): Promise<void> => {
        try {
            const telephoneNumberSetsResponse = await zoomDao.fetchTelephoneNumberSet({audioProviderId: connection.audioProviderId});
            setAudioProviderLabel(telephoneNumberSetsResponse?.name || '');
        } catch (error) {
            alertServerError('Error', error);
        }
    };

    const onLinkClick = (url: string | URL): void => {
        window.open(url, LinkTargetValue.BLANK);
        if (url === hostLink || url === connectionLink) {
            const trackingEventName = url === hostLink
                ? CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.NAVIGATE_HOST_BUTTON
                : CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.NAVIGATE_JOIN_BUTTON;

            trackHostJoinClick(trackingEventName);
        }
    };

    const extendedPopupWidth: number = 225;
    const showCopyAll = connection.type === ConnectionType.Bluejeans ||
        connection.type === ConnectionType.Teams ||
        connection.type === ConnectionType.Other ||
        connection.type === ConnectionType.Webex ||
        connection.type === ConnectionType.Webinar ||
        connection.type === ConnectionType.Zoom ||
        connection.type === ConnectionType.AudioBridge ||
        connection.type === ConnectionType.Knovio ||
        connection.type === ConnectionType.PanelistLink;

    const copyAllMessage = composeConnectionDetailsCopyAllMessage(connection, audioProviderLabel);

    const onClickTroubleshoot = (): void => {
        handleTroubleshoot(connection);
        setIsPopoverOpen(!isPopoverOpen);
    };

    const onCopyBtnClick = (): void => {
        if (handleTrackingEvent) handleTrackingEvent(CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.COPY_DATA);
    };

    return (
        <Popover
            isOpen={isPopoverOpen}
            buttonRef={listItemRef}
            onClickOutside={() => setIsPopoverOpen(false)}
            customWidth={showCopyAll ? extendedPopupWidth : itemWidth}
            padding={0}
            align='start'
        >
            <div className='w-100' onClick={e => e.stopPropagation()}>
                <>
                    <div className='connection-list__popover'>
                        <ConnectionDetailsPopoverContent
                            connection={connection}
                            onLinkClick={onLinkClick}
                            onClickTroubleshoot={onClickTroubleshoot}
                            onCopyBtnClick={onCopyBtnClick}
                            onOpenKnovioPreviewLinkPopover={onOpenKnovioPreviewLinkPopover}
                            webinarReportsVM={webinarReportsVM}
                            audioProviderLabel={audioProviderLabel}
                        />
                        <div className='buttons-block'>
                            <ConnectionDetailsPopoverOptionsButton
                                knovioPreCheckLoading={knovioPreCheckLoading}
                                connection={connection}
                                handleOnChange={handleOnChange}
                                showConfigurePassportForm={showConfigurePassportForm}
                                setIsPopoverOpen={setIsPopoverOpen}
                                isPopoverOpen={isPopoverOpen}
                                setDeletePopoverConnection={setDeletePopoverConnection}
                                cloneConnection={cloneConnection}
                                shouldDisableDeleteButton={shouldDisableDeleteButton}
                            />
                            {showCopyAll &&
                                <CopyButton className='m-left-10px small-button flex center copy-all'
                                    text={<FormattedMessage id='oe.common.copyAll' description='Button text' defaultMessage='Copy all' />}
                                    copyValue={copyAllMessage}
                                    onCopy={onCopyBtnClick}
                                    withoutIcon
                                />
                            }
                        </div>
                    </div>
                </>
            </div>
        </Popover>);
};

export { ConnectionDetailsPopover, TextWithCopyBtn };


